import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, RefreshCw, AlertTriangle, Car, ClipboardList, MessageSquare, Send, ShieldAlert } from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import VerifiedBadge from "../../components/VerifiedBadge";
import { PageLoading } from "../../components/States";
import { api } from "../../lib/api";
import { fmtDateTime, setPageTitle } from "../../lib/format";

const POLL_MS = 20000; // owner-approved: efficient polling, never uncontrolled requests

const inputCls = "w-full rounded-xl border border-[#27272A] bg-[#0A0D10] px-3.5 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E50914] transition-colors";
const KINDS = [["daily_report", "Daily report"], ["incident", "Incident"], ["unauthorized_vehicle", "Unauthorized vehicle"], ["overstay", "Overstay"], ["blocked_space", "Blocked space"], ["visitor_issue", "Visitor issue"], ["access_issue", "Access issue"], ["note", "Note"]];

function stateChip(v) {
  if (v.state === "active") return [`ACTIVE · ${v.minutes_remaining} MIN LEFT`, "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"];
  if (v.state === "ending_soon") return [`ENDING SOON · ${v.minutes_remaining} MIN LEFT`, "text-amber-300 border-amber-500/30 bg-amber-500/10"];
  if (v.state === "expired") return [`EXPIRED · ${v.minutes_over} MIN OVER`, "text-[#FF6B6B] border-[#E50914]/40 bg-[#E50914]/10"];
  return ["UPCOMING", "text-zinc-400 border-[#27272A] bg-white/5"];
}

function FieldInput({ field, value, onChange }) {
  const tid = `report-field-${field.field_id}`;
  if (field.field_type === "dropdown")
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} className={inputCls}>
        <option value="">Select…</option>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  if (field.field_type === "yesno")
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} className={inputCls}>
        <option value="">Select…</option><option value="yes">Yes</option><option value="no">No</option>
      </select>
    );
  return <input type={field.field_type === "number" ? "number" : "text"} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} className={inputCls} />;
}

export default function SecurityDashboard() {
  const { propertyId } = useParams();
  const [ov, setOv] = useState(null);
  const [denied, setDenied] = useState(false);
  const [tab, setTab] = useState("board");
  const [reports, setReports] = useState([]);
  const [fields, setFields] = useState([]);
  const [chat, setChat] = useState([]);
  const [draft, setDraft] = useState("");
  const [form, setForm] = useState({ kind: "daily_report", notes: "", category_code: "", vehicle_ref: "", status: "open" });
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async (silent = true) => {
    try {
      const data = await api.securityOverview(propertyId);
      setOv(data);
      setUpdatedAt(new Date());
      setDenied(false);
    } catch (e) {
      if (e.status === 403 || e.status === 404) setDenied(true);
      else if (!silent) toast.error(e.message);
    }
  }, [propertyId]);

  const loadTab = useCallback(async () => {
    if (tab === "reports") {
      try {
        const [r, f] = await Promise.all([api.securityReports(propertyId), api.securityFields(propertyId)]);
        setReports(r); setFields(f);
      } catch { /* permission handled by overview poll */ }
    } else if (tab === "chat") {
      try { setChat(await api.securityChat(propertyId)); } catch { /* revoked mid-session */ }
    }
  }, [tab, propertyId]);

  useEffect(() => { setPageTitle("Property Security"); load(false); }, [load]);
  useEffect(() => {
    timer.current = setInterval(() => { if (!document.hidden) { load(); loadTab(); } }, POLL_MS);
    return () => clearInterval(timer.current);
  }, [load, loadTab]);
  useEffect(() => { loadTab(); }, [loadTab]);

  const submitReport = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.securityCreateReport(propertyId, { ...form, field_answers: answers });
      toast.success("Report filed — the property owner was notified.");
      setForm({ kind: "daily_report", notes: "", category_code: "", vehicle_ref: "", status: "open" });
      setAnswers({});
      setReports(await api.securityReports(propertyId));
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const sendChat = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    try { await api.securityChatSend(propertyId, draft.trim()); setDraft(""); setChat(await api.securityChat(propertyId)); }
    catch (err) { toast.error(err.message); }
  };

  const canWrite = ov?.permission === "write" || ov?.permission === "owner";

  return (
    <div className="min-h-screen flex flex-col" data-testid="security-dashboard-page">
      <Navbar />
      <main className="flex-1 max-w-6xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <Link to="/security" className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.14em] text-zinc-500 hover:text-white transition-colors" data-testid="security-back">
          <ChevronLeft className="w-4 h-4" /> Shared Properties
        </Link>

        {denied ? (
          <div className="mt-10 card-dark p-10 text-center" data-testid="security-access-denied">
            <ShieldAlert className="w-8 h-8 text-[#FF3B45] mx-auto" />
            <p className="mt-4 font-display text-lg text-white">Access unavailable</p>
            <p className="mt-1 text-sm text-zinc-500">Your security access to this property was revoked or never existed. Contact the property owner.</p>
          </div>
        ) : !ov ? (
          <div className="mt-10"><PageLoading label="Loading live operations…" /></div>
        ) : (
          <>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="type-h1 flex flex-wrap items-center gap-3" data-testid="security-property-name">
                  {ov.property.name || "Property"}
                  <span className="font-mono text-xs tracking-[0.2em] text-[#FF3B45]">#{ov.property.property_code}</span>
                </h1>
                <p className="mt-1 text-sm text-zinc-500">{ov.property.address}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400" data-testid="security-live-indicator">
                  <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" /></span>
                  Live
                </span>
                <span className="text-[11px] text-zinc-500" data-testid="security-last-updated">
                  Last updated {updatedAt ? updatedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—"}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-300 bg-white/5 border border-[#27272A] rounded-full px-2 py-0.5" data-testid="security-permission-chip">{ov.permission}</span>
                <button onClick={() => load(false)} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid="security-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
              </div>
            </div>

            {/* Occupancy */}
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="security-occupancy">
              {[["Occupied", ov.occupancy.occupied, "text-white"], ["Available", ov.occupancy.available, "text-emerald-400"],
                ["Reserved", ov.occupancy.reserved, "text-amber-300"], ["Total capacity", ov.occupancy.capacity, "text-zinc-400"]].map(([l, v, c]) => (
                <div key={l} className="card-dark p-5" data-testid={`occupancy-${l.toLowerCase().replace(/\s+/g, "-")}`}>
                  <p className="meta-label">{l}</p>
                  <p className={`mt-1 font-display text-3xl font-bold tabular-nums ${c}`}>{v}</p>
                </div>
              ))}
            </div>

            {/* Alerts */}
            {ov.alerts.length > 0 && (
              <div className="mt-6 card-dark p-5 border-amber-500/20" data-testid="security-alerts">
                <p className="meta-label !text-amber-300 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" /> Alerts</p>
                <ul className="mt-3 space-y-2">
                  {ov.alerts.map((a, i) => (
                    <li key={i} data-testid={`alert-${a.kind}-${i}`}
                        className={`text-sm flex items-center gap-2 ${a.severity === "critical" ? "text-[#FF6B6B]" : "text-amber-200"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${a.severity === "critical" ? "bg-[#E50914]" : "bg-amber-400"}`} />
                      {a.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tabs */}
            <div className="mt-8 flex gap-1 p-1 rounded-xl border border-[#27272A] bg-[#0D1114]/80 w-fit" role="tablist" data-testid="security-tabs">
              {[["board", "Live Board", Car], ["reports", "Reports", ClipboardList], ["chat", "Chat with Host", MessageSquare]].map(([v, l, Icon]) => (
                <button key={v} role="tab" aria-selected={tab === v} data-testid={`security-tab-${v}`} onClick={() => setTab(v)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold tracking-[0.08em] uppercase transition-colors ${tab === v ? "bg-[#E50914] text-white" : "text-zinc-500 hover:text-zinc-300"}`}>
                  <Icon className="w-3.5 h-3.5" /> {l}
                </button>
              ))}
            </div>

            {tab === "board" && (
              <div className="mt-6" data-testid="security-board">
                {ov.vehicles.length === 0 ? (
                  <div className="card-dark p-10 text-center" data-testid="security-empty-state">
                    <Car className="w-8 h-8 text-zinc-600 mx-auto" />
                    <p className="mt-4 font-display text-lg text-white">No active vehicles at this property</p>
                    <p className="mt-1 text-sm text-zinc-500">The board updates automatically as bookings start and end.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {ov.vehicles.map((v) => {
                      const [label, cls] = stateChip(v);
                      return (
                        <div key={v.booking_id} className="card-dark p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid={`vehicle-row-${v.booking_id}`}>
                          <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider border rounded-full px-2.5 py-1 ${cls}`} data-testid={`vehicle-state-${v.booking_id}`}>{label}</span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-white flex flex-wrap items-center gap-2">
                              {v.vehicle?.plate ? <span className="font-mono tracking-wider">{v.vehicle.plate}</span> : "Vehicle —"}
                              {v.vehicle?.model && <span className="text-zinc-500 font-normal">· {v.vehicle.colour ? `${v.vehicle.colour} ` : ""}{v.vehicle.model}</span>}
                            </p>
                            <p className="mt-0.5 text-xs text-zinc-500">
                              #{v.booking_id.slice(-6)} · {v.category_name || v.category_code || "—"}{v.spot_ref ? ` · Spot ${v.spot_ref}` : ""}
                              {" "}· {fmtDateTime(v.start_time)} → {fmtDateTime(v.end_time)}
                              {v.extended && " · extended"}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-zinc-400">{v.driver_first_name || "Driver"}</span>
                            <VerifiedBadge verified={v.driver_id_verified} testId={`vehicle-verified-${v.booking_id}`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === "reports" && (
              <div className="mt-6 grid lg:grid-cols-2 gap-6" data-testid="security-reports">
                {canWrite && (
                  <form onSubmit={submitReport} className="card-dark p-5 space-y-4 h-fit" data-testid="security-report-form">
                    <p className="meta-label">New report</p>
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1.5">Type</label>
                      <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="report-kind" className={inputCls}>
                        {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-zinc-500 block mb-1.5">Category code (optional)</label>
                        <input value={form.category_code} onChange={(e) => setForm({ ...form, category_code: e.target.value })} data-testid="report-category" className={inputCls} placeholder="101" />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 block mb-1.5">Vehicle ref (optional)</label>
                        <input value={form.vehicle_ref} onChange={(e) => setForm({ ...form, vehicle_ref: e.target.value })} data-testid="report-vehicle" className={inputCls} placeholder="Plate / description" />
                      </div>
                    </div>
                    {fields.map((f) => (
                      <div key={f.field_id}>
                        <label className="text-xs text-zinc-500 block mb-1.5">{f.name}{f.required ? " *" : ""}</label>
                        <FieldInput field={f} value={answers[f.field_id]} onChange={(val) => setAnswers({ ...answers, [f.field_id]: val })} />
                      </div>
                    ))}
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1.5">Notes *</label>
                      <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="report-notes" rows={4} className={inputCls} placeholder="What did you observe?" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1.5">Status</label>
                      <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="report-status" className={inputCls}>
                        <option value="open">Open</option><option value="resolved">Resolved</option><option value="note">Note only</option>
                      </select>
                    </div>
                    <button type="submit" disabled={busy} className="btn-primary w-full text-sm" data-testid="report-submit">{busy ? "Filing…" : "File report"}</button>
                  </form>
                )}
                <div className="space-y-3" data-testid="security-report-list">
                  {!canWrite && (
                    <p className="text-xs text-zinc-500 card-dark p-4" data-testid="reports-readonly-note">You have view access — reports are read-only for your account.</p>
                  )}
                  {reports.length === 0 ? (
                    <div className="card-dark p-8 text-center text-sm text-zinc-500" data-testid="reports-empty">No reports filed yet.</div>
                  ) : reports.map((r) => (
                    <div key={r.report_id} className="card-dark p-4" data-testid={`report-row-${r.report_id}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <p className="text-xs font-bold uppercase tracking-wider text-[#FF3B45]">{r.kind.replace(/_/g, " ")}</p>
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500">{r.status} · {fmtDateTime(r.created_at)}</span>
                      </div>
                      <p className="mt-2 text-sm text-zinc-300 whitespace-pre-line">{r.notes}</p>
                      {(r.vehicle_ref || r.category_code) && (
                        <p className="mt-1.5 text-xs text-zinc-500">{[r.vehicle_ref && `Vehicle: ${r.vehicle_ref}`, r.category_code && `Category: ${r.category_code}`].filter(Boolean).join(" · ")}</p>
                      )}
                      {(r.field_values || []).map((fv) => (
                        <p key={fv.field_id} className="mt-1 text-xs text-zinc-500">{fv.name}: <span className="text-zinc-300">{fv.value}</span></p>
                      ))}
                      <p className="mt-2 text-[11px] text-zinc-600">by {r.author_name || "Security"}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === "chat" && (
              <div className="mt-6 card-dark p-5" data-testid="security-chat">
                <p className="meta-label">Security ↔ Host — scoped to this property only</p>
                <div className="mt-4 space-y-3 max-h-96 overflow-y-auto pr-1" data-testid="security-chat-messages">
                  {chat.length === 0 && <p className="text-sm text-zinc-500" data-testid="security-chat-empty">No messages yet. Introduce yourself to the host.</p>}
                  {chat.map((m) => (
                    <div key={m.message_id} className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm ${m.sender_role === "security" ? "bg-[#E50914]/15 text-zinc-100 ml-auto" : "bg-white/5 text-zinc-300"}`} data-testid={`chat-msg-${m.message_id}`}>
                      <p>{m.text}</p>
                      <p className="mt-1 text-[10px] text-zinc-500">{m.sender_name || m.sender_role} · {fmtDateTime(m.created_at)}</p>
                    </div>
                  ))}
                </div>
                <form onSubmit={sendChat} className="mt-4 flex gap-2">
                  <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Message the host…" data-testid="security-chat-input" className={`${inputCls} flex-1`} />
                  <button type="submit" className="btn-primary !px-4" data-testid="security-chat-send" aria-label="Send"><Send className="w-4 h-4" /></button>
                </form>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
